import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Phone, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  enabled: boolean;
}

interface AgencyStatus {
  keys_set: boolean;
  updated_at: string | null;
  agency_tenants: number;
}

const STATUS_KEY = ['ctm-agency-status'] as const;

/**
 * Steddy's CallTrackingMetrics agency keys. One pair reaches every sub-account,
 * so this is what lets Steddy provision phone numbers for a tenant. The keys
 * are written straight into Vault and never read back.
 */
export function CtmAgencyCard({ enabled }: Props) {
  const queryClient = useQueryClient();
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');

  const statusQuery = useQuery<AgencyStatus | null>({
    queryKey: STATUS_KEY,
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_ctm_agency_status');
      if (error) throw error;
      return ((data ?? [])[0] as AgencyStatus) ?? null;
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('set_ctm_agency_keys', {
        p_access_key: accessKey.trim(),
        p_secret_key: secretKey.trim(),
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      setAccessKey('');
      setSecretKey('');
      queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      toast.success('CTM agency keys saved');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!enabled) return null;
  const status = statusQuery.data;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="h-5 w-5" />
          CallTrackingMetrics agency
        </CardTitle>
        <CardDescription>
          One key pair for the whole agency. It buys and routes numbers for every tenant Steddy manages,
          so tenants never enter CallTrackingMetrics credentials of their own.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {statusQuery.isLoading ? (
            <Badge variant="secondary">
              <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Checking
            </Badge>
          ) : status?.keys_set ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Keys loaded
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-200">
              <AlertCircle className="h-3 w-3 mr-1" /> No keys yet
            </Badge>
          )}
          {status?.keys_set && (
            <span className="text-xs text-muted-foreground">
              {status.agency_tenants} workspace{status.agency_tenants === 1 ? '' : 's'} on the agency account
            </span>
          )}
        </div>

        {!status?.keys_set && (
          <p className="text-sm text-muted-foreground">
            Until these are loaded, no workspace can be provisioned with Steddy-managed numbers.
            Create them in CallTrackingMetrics under Settings, Parent or Agency Settings, API Integration.
          </p>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="ctm-agency-access">Agency access key</Label>
            <Input
              id="ctm-agency-access"
              value={accessKey}
              onChange={(e) => setAccessKey(e.target.value)}
              placeholder={status?.keys_set ? 'Enter a new key to rotate' : 'Access key'}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ctm-agency-secret">Agency secret key</Label>
            <Input
              id="ctm-agency-secret"
              type="password"
              value={secretKey}
              onChange={(e) => setSecretKey(e.target.value)}
              placeholder={status?.keys_set ? 'Enter a new key to rotate' : 'Secret key'}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => save.mutate()}
            disabled={save.isPending || !accessKey.trim() || !secretKey.trim()}
          >
            {save.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving
              </>
            ) : status?.keys_set ? 'Rotate keys' : 'Save keys'}
          </Button>
          <p className="text-xs text-muted-foreground">
            Stored in the vault. They are never shown again after saving.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
